/**
 * The "new rundown" dialog. The name field is local UI state, fresh on every
 * opening; the request is the container's (`onSubmit`),
 * which closes the dialog on success through `onOpenChange`.
 */
import { type FormEvent, useState } from 'react';
import { Button } from './ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog.js';
import { Field, FieldError } from './ui/field.js';
import { Input } from './ui/input.js';
import { Label } from './ui/label.js';

/** The contract's limit on a rundown name (`packages/api-contract/src/rundowns.ts`, `nameSchema`). */
export const RUNDOWN_NAME_MAX = 500;

export type CreateRundownDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSubmit: (name: string) => void;
};

/**
 * The form lives inside DialogContent, which Radix unmounts while the dialog
 * is closed — so every opening starts with a fresh, empty name, and no effect
 * has to reset it.
 */
function CreateRundownForm({ onOpenChange, pending, error, onSubmit }: Omit<CreateRundownDialogProps, 'open'>) {
  const [name, setName] = useState('');
  const trimmed = name.trim();
  const tooLong = trimmed.length > RUNDOWN_NAME_MAX;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (trimmed === '' || tooLong || pending) return;
    onSubmit(trimmed);
  };

  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>New rundown</DialogTitle>
        <DialogDescription>A stand-alone rundown; add it to a playlist later.</DialogDescription>
      </DialogHeader>
      <Field invalid={tooLong || error !== null}>
        <Label htmlFor="rundown-name">Name</Label>
        <Input
          id="rundown-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={pending}
          aria-invalid={tooLong}
          aria-describedby="rundown-name-error"
          autoFocus
        />
        <FieldError id="rundown-name-error">{tooLong ? `At most ${String(RUNDOWN_NAME_MAX)} characters.` : error}</FieldError>
      </Field>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={trimmed === '' || tooLong || pending} data-testid="create-rundown-submit">
          {pending ? 'Creating…' : 'Create'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function CreateRundownDialog({ open, onOpenChange, pending, error, onSubmit }: CreateRundownDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-rundown-dialog">
        <CreateRundownForm onOpenChange={onOpenChange} pending={pending} error={error} onSubmit={onSubmit} />
      </DialogContent>
    </Dialog>
  );
}
